import {DefaultPageProps} from "@models/DefaultProps.ts";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import React, {memo, useEffect, useState} from "react";
import {IDashSolicitacao} from "@models/solicitacoes/dash/IDashSolicitacao.ts";
import {
	DashSoliAgrupamentoFilterTypeValues,
	FilterType,
	IDashSolicitacaoFilters
} from "@models/solicitacoes/dash/IDashSolicitacaoFilters.ts";
import {FormControl, Grid, InputLabel, MenuItem, Select} from "@mui/material";
import {IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import {_DashboardEndpoints} from "@localization/siteMap/endpoints/_DashboardEndpoints.ts";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {ToDate, ToDateDefault, ToMoney} from "@shared/MathHelp.ts";
import {BarChart} from "@mui/x-charts";
import {DefaultDatePicker} from "@shared/custom/DefaultDatePicker.tsx";
import {useApi} from "@contexts/hooks/api/useApi.ts";

export interface DashboardSolicitacaoPageProps extends DefaultPageProps {

}

const SelectedRender = memo(({results, filters}: {
	results: IDashSolicitacao[],
	filters: IDashSolicitacaoFilters,
}) => {
	switch (filters.agrupamento) {
		case 'Dia':
			return <DashboardPorDia values={results}/>
		case 'Mes':
			return <DashboardPorMes values={results}/>
		case 'Recurso':
			return <DashboardPorRecurso values={results}/>
		case 'ContaContabil':
			return <DashboardPorContaContabil values={results}/>
		case 'ContaDia':
			return <DashboardPorContaContabilDia values={results}/>
		case 'ContaMes':
			return <DashboardPorContaContabilMes values={results}/>
		case 'RecursoDia':
			return <DashboardPorRecursoDia values={results}/>
		case 'RecursoMes':
			return <DashboardPorRecursoMes values={results}/>
		default:
			return <Grid container justifyContent={'center'} alignItems={'center'} style={{height: '100%'}}>
				<h2>Selecione um agrupamento para visualizar os dados</h2>
			</Grid>;
	}
})

export function DashboardSolicitacoesPage() {
	const {Get} = useApi()
	const {createAlertFromResponse} = useAlert();
	const [results, setResults] = useState<IDashSolicitacao[]>([]);
	const [filters, setFilters] = useState<IDashSolicitacaoFilters>({agrupamento: 'RecursoMes'});
	const [pagination, setPagination] = useState<IPagination>({page: 1, pageSize: 10});

	useEffect(() => {
		if (pagination.page < 1) {
			setPagination(prev => ({...prev, page: 1}));
			return;
		}

		Get(_DashboardEndpoints.GetSolicitacoes(filters, pagination)).then(x => {
			if (x.status == 'Success') {
				setResults(x.items);
			} else {
				createAlertFromResponse(x)
			}
		})


	}, [pagination.page]);


	return (
		<DefaultContainer titulo={'Solicitações'} size={'large'}>
			<Grid container justifyContent={'space-between'} alignItems={'center'} spacing={2}>
				<Grid size={'grow'}>
					<Grid container spacing={0}>
						<DefaultDatePicker value={filters.dataInicio} onChange={e => {
							setFilters({...filters, dataInicio: e ?? undefined});
							setPagination({...pagination, page: 0});
						}} label={'Data inicial'}/>
						<DefaultDatePicker value={filters.dataFim} onChange={e => {
							setFilters({...filters, dataFim: e ?? undefined});
							setPagination({...pagination, page: 0});

						}} label={'Data final'}/>
					</Grid>
				</Grid>
				<Grid size={"auto"}>
					<FormControl variant={'standard'} fullWidth>
						<InputLabel id="agrupamento-label">Agrupamento</InputLabel>
						<Select value={filters.agrupamento ?? 'RecursoMes'}>
							{DashSoliAgrupamentoFilterTypeValues.map(x => {
								return (
									<MenuItem key={x} value={x} onClick={() => {
										setFilters({...filters, agrupamento: x as FilterType});
										setPagination({...pagination, page: 0});
									}}>
										{x}
									</MenuItem>
								)
							})}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
			<SelectedRender filters={filters} results={results}/>
		</DefaultContainer>
	);
}

function DashboardPorDia(props: { values: IDashSolicitacao[] }) {
	const {values} = props
	return (
		<BarChart height={300}
		          title={"Gastos em solicitações"}
		          barLabel={(i) => ToMoney(i.value)}
		          xAxis={[{
			          scaleType: 'band',
			          data: values.map(x => ToDateDefault(x.data)),
		          }]}
		          series={[
			          {
				          data: values.map((x) => Math.abs(x.valorAprovado ?? 0)),
				          stack: 'APROVADO',
				          label: 'Movimentado',
			          },
			          {
				          data: values.map((x) => Math.abs(x.valorPendente ?? 0)),
				          stack: 'PENDENTE',
				          label: 'Pendente',
			          }
		          ]}
		/>

	)
}

function DashboardPorMes(props: { values: IDashSolicitacao[] }) {
	const {values} = props
	return (
		<BarChart height={300}
		          title={"Gastos em solicitações por mês"}
		          barLabel={(i) => ToMoney(i.value)}
		          xAxis={[{
			          scaleType: 'band',
			          data: values.map(x => ToDate(x.data, 'MMMM')),
		          }]}
		          series={[
			          {
				          data: values.map((x) => Math.abs(x.valorAprovado ?? 0)),
				          stack: 'APROVADO',
				          label: 'Movimentado',
			          },
			          {
				          data: values.map((x) => Math.abs(x.valorPendente ?? 0)),
				          stack: 'PENDENTE',
				          label: 'Pendente',
			          }
		          ]}
		/>
	)
}

function DashboardPorRecurso(props: { values: IDashSolicitacao[] }) {
	const {values} = props
	return (
		<BarChart height={300}
		          title={"Gastos por recurso"}
		          barLabel={(i) => ToMoney(i.value)}
		          xAxis={[{
			          scaleType: 'band',
			          data: values.map(x => x.nomeRecurso ?? 'N/A'),
		          }]}
		          series={[
			          {
				          data: values.map((x) => Math.abs(x.valorAprovado ?? 0)),
				          stack: 'APROVADO',
				          label: 'Movimentado',
			          },
			          {
				          data: values.map((x) => Math.abs(x.valorPendente ?? 0)),
				          stack: 'PENDENTE',
				          label: 'Pendente',
			          }
		          ]}
		/>
	)
}

function DashboardPorContaContabil(props: { values: IDashSolicitacao[] }) {
	const {values} = props
	return (
		<BarChart height={300}
		          title={"Gastos por conta contábil"}
		          barLabel={(i) => ToMoney(i.value)}
		          xAxis={[{
			          scaleType: 'band',
			          data: values.map(x => x.nomeContaContabil ?? 'N/A'),
		          }]}
		          series={[
			          {
				          data: values.map((x) => Math.abs(x.valorAprovado ?? 0)),
				          stack: 'APROVADO',
				          label: 'Movimentado',
			          },
			          {
				          data: values.map((x) => Math.abs(x.valorPendente ?? 0)),
				          stack: 'PENDENTE',
				          label: 'Pendente',
			          }
		          ]}
		/>
	)
}

function DashboardPorContaContabilDia(props: { values: IDashSolicitacao[] }) {
	const {values} = props
	return (
		<BarChart height={300}
		          title={"Gastos por conta contábil e dia"}
		          barLabel={(i) => ToMoney(i.value)}
		          xAxis={[{
			          scaleType: 'band',
			          data: values.map(x => `${ToDateDefault(x.data)} ${x.nomeContaContabil}`),
		          }]}
		          series={[
			          {
				          data: values.map((x) => Math.abs(x.valorAprovado ?? 0)),
				          stack: 'APROVADO',
				          label: 'Movimentado',
			          },
			          {
				          data: values.map((x) => Math.abs(x.valorPendente ?? 0)),
				          stack: 'PENDENTE',
				          label: 'Pendente',
			          }
		          ]}
		/>
	)
}

function DashboardPorContaContabilMes(props: { values: IDashSolicitacao[] }) {
	const {values} = props
	return (
		<BarChart height={300}
		          title={"Gastos por conta contábil e mês"}
		          barLabel={(i) => ToMoney(i.value)}
		          xAxis={[{
			          scaleType: 'band',
			          data: values.map(x => `${ToDate(x.data, 'MMMM')} ${x.nomeContaContabil} `),
		          }]}
		          series={[
			          {
				          data: values.map((x) => Math.abs(x.valorAprovado ?? 0)),
				          stack: 'APROVADO',
				          label: 'Movimentado',
			          },
			          {
				          data: values.map((x) => Math.abs(x.valorPendente ?? 0)),
				          stack: 'PENDENTE',
				          label: 'Pendente',
			          }
		          ]}
		/>
	)
}

function DashboardPorRecursoDia(props: { values: IDashSolicitacao[] }) {
	const {values} = props
	return (
		<BarChart height={300}
		          title={"Gastos por recurso e dia"}
		          barLabel={(i) => ToMoney(i.value)}
		          xAxis={[{
			          scaleType: 'band',
			          data: values.map(x => `${ToDateDefault(x.data)} ${x.nomeRecurso}`),
		          }]}
		          series={[
			          {
				          data: values.map((x) => Math.abs(x.valorAprovado ?? 0)),
				          stack: 'APROVADO',
				          label: 'Movimentado',
			          },
			          {
				          data: values.map((x) => Math.abs(x.valorPendente ?? 0)),
				          stack: 'PENDENTE',
				          label: 'Pendente',
			          }
		          ]}
		/>
	)
}

function DashboardPorRecursoMes(props: { values: IDashSolicitacao[] }) {
	const {values} = props
	return (
		<BarChart height={300}
		          title={"Gastos por recurso e mês"}
		          barLabel={(i) => ToMoney(i.value)}
		          xAxis={[{
			          scaleType: 'band',
			          data: values.map(x => `${ToDate(x.data, 'MMMM')} ${x.nomeRecurso}`),
		          }]}
		          series={[
			          {
				          data: values.map((x) => Math.abs(x.valorAprovado ?? 0)),
				          stack: 'APROVADO',
				          label: 'Movimentado',
			          },
			          {
				          data: values.map((x) => Math.abs(x.valorPendente ?? 0)),
				          stack: 'PENDENTE',
				          label: 'Pendente',
			          }
		          ]}
		/>
	)
}